import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const PreferencesTab = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    security: true,
    sharing: false,
    marketing: false
  });
  const [accessibility, setAccessibility] = useState({
    highContrast: false,
    largeText: false,
    reducedMotion: false,
    screenReader: false
  });
  const [autoLock, setAutoLock] = useState('15');
  const [clipboardTimeout, setClipboardTimeout] = useState('30');

  const languageOptions = [
    { value: 'en', label: 'English (US)' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'ja', label: '日本語' },
    { value: 'zh', label: '中文' }
  ];

  const timezoneOptions = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (UK)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
    { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (DE)' }
  ];

  const autoLockOptions = [
    { value: '5', label: '5 minutes' },
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '240', label: '4 hours' },
    { value: 'never', label: 'Never' }
  ];

  const clipboardTimeoutOptions = [
    { value: '10', label: '10 seconds' },
    { value: '30', label: '30 seconds' },
    { value: '60', label: '1 minute' },
    { value: '300', label: '5 minutes' },
    { value: 'never', label: 'Never clear' }
  ];

  const handleNotificationChange = (key, checked) => {
    setNotifications(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleAccessibilityChange = (key, checked) => {
    setAccessibility(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleSavePreferences = () => {
    console.log('Saving preferences...', {
      darkMode,
      language,
      timezone,
      dateFormat,
      notifications,
      accessibility,
      autoLock,
      clipboardTimeout
    });
  };

  return (
    <div className="space-y-8">
      {/* Interface Preferences */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Palette" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Interface</h3>
        </div>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Dark Mode</h4>
              <p className="text-sm text-muted-foreground">Use dark theme across the application</p>
            </div>
            <Checkbox
              checked={darkMode}
              onChange={(e) => setDarkMode(e?.target?.checked)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Language"
              options={languageOptions}
              value={language}
              onChange={setLanguage}
            />
            
            <Select
              label="Timezone"
              options={timezoneOptions}
              value={timezone}
              onChange={setTimezone}
              searchable
            />
            
            <Select
              label="Date Format"
              options={dateFormatOptions}
              value={dateFormat}
              onChange={setDateFormat}
            />
          </div>
        </div>
      </div>
      {/* Notification Preferences */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Bell" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Email Notifications</h4>
              <p className="text-sm text-muted-foreground">Receive notifications via email</p>
            </div>
            <Checkbox
              checked={notifications?.email}
              onChange={(e) => handleNotificationChange('email', e?.target?.checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Push Notifications</h4>
              <p className="text-sm text-muted-foreground">Browser and mobile push notifications</p>
            </div>
            <Checkbox
              checked={notifications?.push}
              onChange={(e) => handleNotificationChange('push', e?.target?.checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Security Alerts</h4>
              <p className="text-sm text-muted-foreground">Important security and breach notifications</p>
            </div>
            <Checkbox
              checked={notifications?.security}
              onChange={(e) => handleNotificationChange('security', e?.target?.checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Sharing Updates</h4>
              <p className="text-sm text-muted-foreground">When items are shared with you or access changes</p>
            </div>
            <Checkbox
              checked={notifications?.sharing}
              onChange={(e) => handleNotificationChange('sharing', e?.target?.checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Marketing Communications</h4>
              <p className="text-sm text-muted-foreground">Product updates and promotional content</p>
            </div>
            <Checkbox
              checked={notifications?.marketing}
              onChange={(e) => handleNotificationChange('marketing', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Security Preferences */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Shield" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Security</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Auto-lock Timeout"
            description="Automatically lock the vault after inactivity"
            options={autoLockOptions}
            value={autoLock}
            onChange={setAutoLock}
          />
          
          <Select
            label="Clipboard Timeout"
            description="Clear copied passwords from clipboard"
            options={clipboardTimeoutOptions}
            value={clipboardTimeout}
            onChange={setClipboardTimeout}
          />
        </div>
      </div>
      {/* Accessibility */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Eye" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Accessibility</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">High Contrast Mode</h4>
              <p className="text-sm text-muted-foreground">Increase contrast for better visibility</p>
            </div>
            <Checkbox
              checked={accessibility?.highContrast}
              onChange={(e) => handleAccessibilityChange('highContrast', e?.target?.checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Large Text</h4>
              <p className="text-sm text-muted-foreground">Increase font size throughout the application</p>
            </div>
            <Checkbox
              checked={accessibility?.largeText}
              onChange={(e) => handleAccessibilityChange('largeText', e?.target?.checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Reduced Motion</h4>
              <p className="text-sm text-muted-foreground">Minimize animations and transitions</p>
            </div>
            <Checkbox
              checked={accessibility?.reducedMotion}
              onChange={(e) => handleAccessibilityChange('reducedMotion', e?.target?.checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Screen Reader Support</h4>
              <p className="text-sm text-muted-foreground">Enhanced compatibility with screen readers</p>
            </div>
            <Checkbox
              checked={accessibility?.screenReader}
              onChange={(e) => handleAccessibilityChange('screenReader', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={handleSavePreferences}
          iconName="Save"
          iconPosition="left"
        >
          Save Preferences
        </Button>
      </div>
    </div>
  );
};

export default PreferencesTab;